import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';

import { AppState } from '.';
import {
  exceptionOf,
  Feature,
  Operator,
  Query,
  SerializedException,
  Subscription,
  SubscriptionSource,
} from '../models';
import { SubscriptionService } from '../services';
import { SubscriptionStatus } from '../models/subscriptionStatus';

export const SUBSCRIPTION_FEATURE_KEY = 'subscription';

const subscriptionAdapter = createEntityAdapter<Subscription>();

interface SubscriptionState extends EntityState<Subscription> {
  // Indicates if the user had any subscription before
  hadSubscription: boolean;
  hadPaidSubscription: boolean;
  // The BE's new-account verdict (`GET /v1/subscriptions/is-access-starter-package` ->
  // SubscriptionService.isNewAccount). `undefined` = not answered yet OR the call failed, and
  // callers must treat it as "unknown" and hide the offer (fail-closed): showing a Starter
  // bundle to an account the BE will reject is a dead-end checkout.
  isNewAccount?: boolean;
  newAccountStatus: 'idle' | 'loading' | 'resolved';
  loading: boolean;
  errors: SerializedException[];
}

const createInitialState = (): SubscriptionState => subscriptionAdapter.getInitialState({
  hadSubscription: false,
  hadPaidSubscription: false,
  isNewAccount: undefined as boolean | undefined,
  newAccountStatus: 'idle' as 'idle' | 'loading' | 'resolved',
  loading: true,
  errors: [],
});

export const doFetchSubscriptions = createAsyncThunk(
  'subscription/fetchSubscription',
  async (input: { accountId: number }) => {
    const { accountId } = input;
    const criteria: Query<Subscription>[] = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: accountId,
      },
      {
        field: 'status',
        operator: Operator.EQ,
        value: SubscriptionStatus.ACTIVE,
      },
    ];
    const subscriptionResponse = await SubscriptionService.getSubscriptions(...criteria);
    return { subscriptionResponse };
  },
);

/**
 * Asks the BE whether this account still counts as a new account (Decision 6). Runs at most once
 * per store: `condition` short-circuits a second dispatch while the first is in flight or after
 * it resolved, so a failed call cannot turn into a refetch loop.
 */
export const doCheckNewAccount = createAsyncThunk(
  'subscription/checkNewAccount',
  async (accountId: number) => SubscriptionService.isAccessStarterPackage(accountId),
  {
    condition: (_accountId, { getState }) => (
      (getState() as AppState)[SUBSCRIPTION_FEATURE_KEY].newAccountStatus === 'idle'
    ),
  },
);

export const doToggleAutoRenew = createAsyncThunk(
  'subscriptions/toggleAutoRenew',
  async (recurlySubscriptionId: number, { rejectWithValue }) => {
    try {
      const autoRenew = await SubscriptionService.toggleAutoRenew(recurlySubscriptionId);
      return autoRenew;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const subscriptionSlice = createSlice({
  name: SUBSCRIPTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    // A successful checkout can end the account's first-purchase window, but doCheckNewAccount
    // asks the BE at most once per store — so without this, an SPA navigation back to /plan
    // still shows the Starter layout and every bundle preview 400s
    // (`bundle.requires.new.account`). Dropping the verdict makes the next visit ask again.
    invalidateNewAccount: state => {
      state.isNewAccount = undefined;
      state.newAccountStatus = 'idle';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(doFetchSubscriptions.pending, state => {
        state.loading = true;
      })
      .addCase(doFetchSubscriptions.fulfilled, (state, action) => {
        state.hadSubscription = action.payload.subscriptionResponse.length > 0;
        state.hadPaidSubscription = action.payload.subscriptionResponse.filter(
          sub => sub.source === SubscriptionSource.RECURLY,
        ).length > 0;

        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const subscriptions = action.payload.subscriptionResponse.filter(
          sub => sub.status === SubscriptionStatus.ACTIVE
            && sub.archived === false
            && sub.expiryDate > sevenDaysAgo,
        );
        subscriptionAdapter.setAll(state, subscriptions);
        state.loading = false;
      })
      .addCase(doFetchSubscriptions.rejected, (state, action) => {
        const payload = action.payload as SerializedException;
        state.loading = false;
        state.errors.push(payload);
      })
      .addCase(doCheckNewAccount.pending, state => {
        state.newAccountStatus = 'loading';
      })
      .addCase(doCheckNewAccount.fulfilled, (state, action) => {
        state.newAccountStatus = 'resolved';
        state.isNewAccount = action.payload === true;
      })
      .addCase(doCheckNewAccount.rejected, state => {
        // Fail-closed: no verdict means no offer. Deliberately NOT falling back to a local
        // guess from /v2/subscriptions — that list holds ACTIVE subscriptions only, so an
        // account whose subscription expired last month would look brand new here while the
        // BE (6-month window) refuses the purchase.
        state.newAccountStatus = 'resolved';
        state.isNewAccount = undefined;
        // eslint-disable-next-line no-console
        console.warn('[subscription] new-account check failed; hiding first-purchase offers');
      })
      .addCase(doToggleAutoRenew.pending, state => {
        state.loading = true;
      })
      .addCase(doToggleAutoRenew.fulfilled, state => {
        state.loading = false;
      })
      .addCase(doToggleAutoRenew.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      });
  },
});

const selectSubscriptionFeature = (state: AppState) => state[SUBSCRIPTION_FEATURE_KEY];
const { selectAll } = subscriptionAdapter.getSelectors(selectSubscriptionFeature);

export const selectLoading = createSelector(
  selectSubscriptionFeature,
  subState => subState.loading,
);

export const selectErrors = createSelector(
  selectSubscriptionFeature,
  subState => subState.errors,
);

export const selectByAccountId = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(it => it.accountId === accountId),
);

export const selectSubscriptionsBySource = (
  source: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions
    .filter(it => it.source === source),
);

const isTrueSubscription = (subscription: Subscription, source?: SubscriptionSource) => (
  (!source || subscription.source === source)
  && (
    subscription.feature === Feature.TRUE_TEAM
    || subscription.feature === Feature.TRUE_ENTERPRISE
    || subscription.feature === Feature.TRUE_PLATFORM_ONLY
    || subscription.feature === Feature.TRUE_AUTOMATION
  )
);

export const selectTrueSubscriptionList = (
  source?: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(subscription => isTrueSubscription(subscription, source)),
);

export const selectTrueSubscriptions = (
  source?: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions.find(subscription => isTrueSubscription(subscription, source)),
);

/**
 * The BE's new-account verdict for first-purchase-only offers (True Bundle Starters, starter
 * package). `undefined` until `doCheckNewAccount` has answered, and after a failed call: callers
 * must show the offer ONLY on an explicit `true` (Decision 6 — FE and BE run the same rule).
 */
export const selectIsNewAccount = createSelector(
  selectSubscriptionFeature,
  subState => subState.isNewAccount,
);

/**
 * Lifecycle of `doCheckNewAccount`: anything but `'resolved'` means the verdict above is not
 * final yet, so a layout that depends on it should hold (skeleton) instead of guessing.
 */
export const selectNewAccountStatus = createSelector(
  selectSubscriptionFeature,
  subState => subState.newAccountStatus,
);

export const hasPaidSubscription = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.some(it => it.accountId === accountId
    && it.source === SubscriptionSource.RECURLY),
);

export const { invalidateNewAccount } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
